import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Loader2, Inbox, Trash2, CheckCircle2, Clock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TableToolbar from "@/components/shared/TableToolbar";
import TablePagination from "@/components/shared/TablePagination";
import { adminMembersApi } from "@/lib/api/adminMembers";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/lib/roles";
import { formatApiErrorDetail } from "@/lib/api";
import CreateMemberDialog from "@/components/admin/CreateMemberDialog";
import EditMemberDialog from "@/components/admin/EditMemberDialog";

function useDebounce(value, delay = 250) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

function StatusBadge({ row, t }) {
    if (!row.is_active) return <Badge variant="secondary">{t("admin.members.statusDisabled")}</Badge>;
    if (row.activation_status === "PENDING_PAYMENT") {
        return <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">{t("admin.members.statusPendingPayment")}</Badge>;
    }
    if (row.activation_status === "PENDING_REVIEW") {
        return <Badge className="bg-warning text-white">{t("admin.members.statusPendingReview")}</Badge>;
    }
    if (row.is_expired) return <Badge className="bg-destructive text-destructive-foreground">{t("admin.members.statusExpired")}</Badge>;
    if (row.is_expiring_soon) return <Badge className="bg-warning text-white">{t("admin.members.statusExpiring", { days: row.days_to_expiry })}</Badge>;
    return <Badge variant="outline" className="border-success/40 text-success">{t("admin.members.statusActive")}</Badge>;
}

function TierBadge({ tier }) {
    const tone = tier === "PLATINUM" ? "bg-indigo-100 text-indigo-700"
              : tier === "GOLD" ? "bg-amber-100 text-amber-700"
              : "bg-zinc-100 text-zinc-700";
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${tone}`}>{tier}</span>;
}

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB",
            { day: "2-digit", month: "short", year: "numeric" });
    } catch (_) { return iso; }
}

export default function MembersPage() {
    const { t, lang } = useI18n();
    const { user } = useAuth();
    const canEdit = [ROLES.SUPER_ADMIN, ROLES.OPS_ADMIN].includes(user?.role);

    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [sortValue, setSortValue] = useState("created_desc");
    const [dateRange, setDateRange] = useState({ from: "", to: "" });
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const debounced = useDebounce(query, 300);

    const onDelete = async () => {
        if (!deleting) return;
        setDeleteSubmitting(true);
        try {
            await adminMembersApi.delete(deleting.id);
            setItems((prev) => prev?.filter((m) => m.id !== deleting.id) || prev);
            setTotal((p) => Math.max(0, p - 1));
            toast.success(t("admin.members.deleted", { name: deleting.full_name }));
            setDeleting(null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setDeleteSubmitting(false);
        }
    };

    const [activatingId, setActivatingId] = useState(null);
    const activateMember = async (row) => {
        setActivatingId(row.id);
        try {
            const updated = await adminMembersApi.update(row.id, { activation_status: "ACTIVE" });
            setItems((prev) => prev?.map((m) => (m.id === row.id ? updated : m)) || prev);
            toast.success(t("admin.members.activated", { name: row.full_name }));
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setActivatingId(null);
        }
    };

    const pendingReviewCount = (items || []).filter((m) => m.activation_status === "PENDING_REVIEW").length;

    const load = useCallback(async () => {
        try {
            const params = {
                q: debounced || undefined,
                tier: filterValues.tier || undefined,
                activation_status: filterValues.activation_status || undefined,
                date_from: dateRange.from || undefined,
                date_to: dateRange.to || undefined,
                sort: sortValue || undefined,
                limit: pageSize,
                skip: (page - 1) * pageSize,
            };
            const res = await adminMembersApi.list(params);
            setItems(res.items);
            setTotal(res.total);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    }, [debounced, filterValues, dateRange.from, dateRange.to, sortValue, pageSize, page]);

    useEffect(() => { load(); }, [load]);
    // Reset page when filters change
    useEffect(() => { setPage(1); }, [debounced, filterValues, dateRange.from, dateRange.to, sortValue, pageSize]);

    const onCreated = (newMember) => {
        toast.success(t("admin.members.created"));
        // Optimistically prepend the new row.
        setItems((prev) => [newMember, ...(prev || [])]);
        setTotal((p) => p + 1);
    };

    const onSaved = (updated) => {
        setItems((prev) => prev?.map((m) => (m.id === updated.id ? updated : m)) || prev);
    };

    return (
        <div className="max-w-7xl space-y-6 fade-up" data-testid="page-admin-members">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("admin.members.title")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{t("admin.members.subtitle")}</p>
                </div>
                <Button onClick={() => setCreateOpen(true)} data-testid="add-member-button">
                    <Plus className="h-4 w-4 me-1" />
                    {t("admin.members.add")}
                </Button>
            </header>

            <TableToolbar
                search={query}
                onSearchChange={setQuery}
                searchPlaceholder={t("admin.members.searchPlaceholder")}
                filters={[
                    {
                        key: "activation_status",
                        label: t("admin.members.fields.activationStatus"),
                        options: [
                            { value: "PENDING_PAYMENT", label: t("admin.members.statusPendingPayment") },
                            { value: "PENDING_REVIEW", label: t("admin.members.statusPendingReview") },
                            { value: "ACTIVE", label: t("admin.members.statusActive") },
                        ],
                    },
                    {
                        key: "tier",
                        label: t("admin.members.fields.tier"),
                        options: [
                            { value: "SILVER", label: t("admin.members.tier.SILVER") },
                            { value: "GOLD", label: t("admin.members.tier.GOLD") },
                            { value: "PLATINUM", label: t("admin.members.tier.PLATINUM") },
                        ],
                    },
                ]}
                filterValues={filterValues}
                onFilterChange={(k, v) => setFilterValues((p) => ({ ...p, [k]: v }))}
                onClearFilters={() => setFilterValues({})}
                sortOptions={[
                    { value: "created_desc", label: t("table.sort.createdDesc") },
                    { value: "created_asc", label: t("table.sort.createdAsc") },
                    { value: "expiry_asc", label: t("table.sort.endDateAsc") },
                ]}
                sortValue={sortValue}
                onSortChange={setSortValue}
                showDates
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
            />

            {pendingReviewCount > 0 && (
                <div className="rounded-2xl border border-warning/40 bg-warning/5 px-5 py-3 flex items-center gap-3" data-testid="members-pending-review-banner">
                    <Clock className="h-5 w-5 text-warning shrink-0" />
                    <p className="text-sm">
                        <span className="font-semibold">{t("admin.members.pendingReviewBannerTitle", { n: pendingReviewCount })}</span>
                        <span className="text-muted-foreground ms-1.5">{t("admin.members.pendingReviewBannerHint")}</span>
                    </p>
                </div>
            )}

            {items === null && (
                <div className="surface-card p-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}

            {items && items.length === 0 && (
                <div className="surface-card p-10 text-center" data-testid="members-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">{t("admin.members.empty")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("admin.members.emptyHint")}</p>
                </div>
            )}

            {items && items.length > 0 && (
                <div className="surface-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="members-table">
                            <thead className="bg-muted/30">
                                <tr className="text-start">
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.members.cols.member")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.members.cols.code")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.members.cols.tier")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.members.cols.expiry")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.members.cols.status")}</th>
                                    <th className="px-5 py-3 font-semibold text-end">{t("admin.members.cols.actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="border-t border-border hover:bg-muted/20"
                                        data-testid={`member-row-${row.id}`}
                                    >
                                        <td className="px-5 py-3">
                                            <div className="font-semibold">{row.full_name}</div>
                                            <div className="text-xs text-muted-foreground">{row.email || "—"}</div>
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs">{row.member_code}</td>
                                        <td className="px-5 py-3"><TierBadge tier={row.tier} /></td>
                                        <td className="px-5 py-3">
                                            <div className="text-sm">{fmtDate(row.expiry_date, lang)}</div>
                                            {row.days_to_expiry !== null && row.days_to_expiry !== undefined && (
                                                <div className={`text-xs ${row.is_expired ? "text-destructive" : (row.is_expiring_soon ? "text-destructive" : "text-muted-foreground")}`}>
                                                    {row.is_expired
                                                        ? t("admin.members.expiredDays", { days: Math.abs(row.days_to_expiry) })
                                                        : t("admin.members.daysLeft", { days: row.days_to_expiry })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <StatusBadge row={row} t={t} />
                                            {row.payment_reference && row.activation_status !== "ACTIVE" && (
                                                <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground font-mono" title={t("admin.members.paymentReferenceLabel")}>
                                                    <CreditCard className="h-3 w-3" />
                                                    <span className="truncate max-w-[140px]">{row.payment_reference}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-end">
                                            {canEdit && (
                                                <div className="inline-flex items-center gap-1">
                                                    {row.activation_status === "PENDING_REVIEW" && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => activateMember(row)}
                                                            disabled={activatingId === row.id}
                                                            className="bg-success hover:bg-success/90 text-white"
                                                            data-testid={`member-activate-${row.id}`}
                                                        >
                                                            {activatingId === row.id
                                                                ? <Loader2 className="h-4 w-4 me-1 animate-spin" />
                                                                : <CheckCircle2 className="h-4 w-4 me-1" />}
                                                            {t("admin.members.activate")}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setEditing(row)}
                                                        data-testid={`member-edit-${row.id}`}
                                                    >
                                                        <Pencil className="h-4 w-4 me-1" />
                                                        {t("admin.members.edit")}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setDeleting(row)}
                                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        data-testid={`member-delete-${row.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination
                        total={total}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                    />
                </div>
            )}

            <CreateMemberDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={onCreated}
            />
            <EditMemberDialog
                open={!!editing}
                onOpenChange={(o) => !o && setEditing(null)}
                member={editing}
                onSaved={onSaved}
            />

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && !deleteSubmitting && setDeleting(null)}>
                <AlertDialogContent data-testid="member-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("admin.members.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("admin.members.confirmDeleteBody", { name: deleting?.full_name || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteSubmitting}>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={deleteSubmitting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="member-delete-confirm-button"
                        >
                            {deleteSubmitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
